import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
    APP_URL_HOME,
    STORAGE_KEY_ACCESS_TOKEN,
    STORAGE_KEY_CHATS_MESSAGES_COUNT,
    STORAGE_KEY_SAVED_USER_MESSAGES,
    STORE_AUTH_PAYMENT_POPUP
} from '../../const';
import AuthApi from '../../service/Api/authApi';
import storageService from '../../service/StorageService';
import WSService from '../../service/WSService';
import webSocketConnector from '../../service/WSService/WebSocketConnector';
import { gaEvents } from '../../utils/analytics';

export const FilledDataType = { COMPLETE: 'complete', INCOMPLETE: 'incomplete' };

const setPaymentPopupState = (plan, limits) => {
    if (!plan || !limits) {
        return STORE_AUTH_PAYMENT_POPUP.product;
    }

    const { isFree, isTrial, isCancel } = plan;
    const { total, used } = limits;

    if (isFree && !isTrial) {
        return STORE_AUTH_PAYMENT_POPUP.trial;
    }
    if (isFree && isTrial && isCancel) {
        return STORE_AUTH_PAYMENT_POPUP.plan;
    }
    if (isFree && isTrial && !isCancel) {
        return STORE_AUTH_PAYMENT_POPUP.issue;
    }
    if (!isFree && total === used) {
        return STORE_AUTH_PAYMENT_POPUP.product;
    }
    return STORE_AUTH_PAYMENT_POPUP.product;
};

const initialState = {
    showAuthModal: false,
    isAuthModalCanBeClosed: true,
    user: {
        id: '',
        dataStatus: FilledDataType.INCOMPLETE,
        isRegister: false,
        name: '',
        avatar: '',
        birthDate: '',
        birthPlace: '',
        chatIds: [],
        email: '',
        phone: '',
        authStatus: '',
        unreadMessage: 0,
        plan: {
            isAutoRenewal: false,
            isFree: true,
            isTrial: false,
            isCancel: false,
            id: '',
            validUntil: ''
        },
        paymentPopup: 'trial',
        limits: {
            planTotal: 0,
            total: 20,
            used: 0
        }
    },
    accessToken: '',
    refreshToken: '',
    loading: false,
    error: null,
    registerStepCount: 1,
    location: {
        loading: false,
        list: [],
        error: null
    }
};

export const fetchUser = createAsyncThunk('auth/fetchUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.getUser();
        return data.data.user;
    } catch (error) {
        return rejectWithValue({ message: error.message, status: error.response.status });
    }
});

export const authUser = createAsyncThunk('auth/authUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.getUser(params);
        return data.data.user;
    } catch (error) {
        return rejectWithValue({ message: error.message, status: error.response.status });
    }
});

export const updateUser = createAsyncThunk('auth/updateUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.updateUser(params);
        return data.data.user;
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

export const deleteUser = createAsyncThunk('auth/deleteUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.deleteUser();
        return data.data.user;
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

export const fetchLocations = createAsyncThunk('auth/fetchLocations', async (search, { rejectWithValue }) => {
    try {
        const data = await AuthApi.getUserLocation(search);
        return data.data;
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

export const logOut = createAsyncThunk('auth/logout', async (forceLogOut, { rejectWithValue }) => {
    try {
        if (!forceLogOut) {
            await AuthApi.userLogout();
        }
        return {
            forceLogOut
        };
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

export const getAuthModalState = (state) => state.auth.showAuthModal;
export const getAuthModalClosingPossibility = (state) => state.auth.isAuthModalCanBeClosed;
export const getUser = (state) => state.auth.user;
export const getToken = (state) => state.accessToken;
export const getUserId = (state) => state.auth.user.id;
export const getUserUnreadMessages = (state) => state.auth.user.unreadMessage;
export const getUserDataStatus = (state) => state.auth.user.dataStatus;
export const getUserPlan = (state) => state.auth.user.plan;
export const getUserLimits = (state) => state.auth.user.limits;
export const getUserPaymentPopupState = (state) => state.auth.user.paymentPopup;

export const getRegisterStepCount = (state) => state.auth.registerStepCount;

export const getAuthError = (state) => state.auth.error;
export const getUserLoadingState = (state) => state.auth.loading;
export const getBirthLocation = (state) => state.auth.location;
export const getUserAccessToken = (state) => state.auth.accessToken;

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        toggleAuthModal(state, { payload }) {
            const { trigger, step = 1 } = payload;
            state.showAuthModal = trigger;
            state.registerStepCount = step;
        },
        setAuthModalCanBeClosed(state, { payload }) {
            state.isAuthModalCanBeClosed = payload;
        },
        setUserStatusType(state, { payload }) {
            state.user.dataStatus = payload;
        },
        setAuthToken(state, { payload }) {
            state.accessToken = payload;
        },
        setAuthRegisterStepCount(state, { payload }) {
            state.registerStepCount = payload;
        },
        clearAuthError(store) {
            store.error = null;
        },
        reduceUserUnreadMessages(store, { payload }) {
            store.user.unreadMessage = store.user.unreadMessage - payload < 0 ? 0 : store.user.unreadMessage - payload;
        },
        setUserMessagesLimits(state, { payload }) {
            state.user.limits = { ...payload };
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUser.pending, (store) => {
                store.loading = true;
            })
            .addCase(fetchUser.fulfilled, (store, { payload }) => {
                store.loading = false;
                store.accessToken = payload?.accessToken;
                store.refreshToken = payload?.refreshToken;

                WSService.init();
                webSocketConnector.init(storageService.get(STORAGE_KEY_ACCESS_TOKEN));

                store.user = {
                    ...store.user,
                    id: payload.id,
                    name: payload.name,
                    avatar: payload.avatar,
                    birthDate: payload.birthDate,
                    chatIds: payload.chatIds,
                    email: payload?.email,
                    birthAddress: payload?.birthAddress,
                    dataStatus: payload.dataStatus,
                    phone: payload?.phone,
                    authStatus: payload?.authStatus,
                    unreadMessage: payload?.unreadMessage,
                    plan: {
                        ...payload?.plan
                    },
                    limits: {
                        ...payload?.limits
                    },
                    paymentPopup: setPaymentPopupState(payload?.plan, payload?.limits)
                };
            })
            .addCase(fetchUser.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(authUser.pending, (store) => {
                store.loading = true;
            })
            .addCase(authUser.fulfilled, (store, { payload }) => {
                const authCompleted = payload?.dataStatus === FilledDataType.COMPLETE;
                const isSignUp = payload?.dateUpdated === payload?.dateCreated;
                isSignUp ? gaEvents.signUp(payload?.id) : gaEvents.login(payload?.id);
                store.loading = false;
                store.registerStepCount = authCompleted ? 1 : 2;
                store.isRegister = payload.isRegister;
                store.user = {
                    ...store.user,
                    id: payload.id,
                    dataStatus: payload.dataStatus,
                    name: payload.name,
                    avatar: payload.avatar,
                    chatIds: payload.chatIds,
                    email: payload?.email,
                    birthAddress: payload?.birthAddress,
                    phone: payload?.phone,
                    isRegister: payload?.isRegister,
                    authStatus: payload?.authStatus,
                    dateUpdated: payload?.dateUpdated,
                    unreadMessage: payload?.unreadMessage,
                    plan: {
                        ...payload?.plan
                    },
                    limits: {
                        ...payload?.limits
                    },
                    paymentPopup: setPaymentPopupState(payload?.plan, payload?.limits)
                };
            })
            .addCase(authUser.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
                store.showAuthModal = true;
            })
            .addCase(updateUser.pending, (store) => {
                store.loading = true;
            })
            .addCase(updateUser.fulfilled, (store, { payload }) => {
                const authCompleted = payload.dataStatus === FilledDataType.COMPLETE;
                store.loading = false;
                store.registerStepCount = authCompleted ? 1 : 2;
                store.user = {
                    ...store.user,
                    dataStatus: payload?.dataStatus,
                    name: payload.name,
                    birthDate: payload.birthDate,
                    chatIds: payload.chatIds,
                    email: payload?.email,
                    birthAddress: payload?.birthAddress,
                    phone: payload?.phone,
                    dateUpdated: payload?.dateUpdated,
                    unreadMessage: payload?.unreadMessage
                };
            })
            .addCase(updateUser.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(deleteUser.pending, (store) => {
                store.loading = true;
                store.error = null;
            })
            .addCase(deleteUser.fulfilled, (store, { payload }) => {
                gaEvents.delete(payload?.id);
                store = initialState;
                storageService.clear();
                window.location.replace(APP_URL_HOME);
            })
            .addCase(deleteUser.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(fetchLocations.pending, (store) => {
                store.location.loading = true;
                store.location.error = null;
            })
            .addCase(fetchLocations.fulfilled, (store, { payload }) => {
                store.location.loading = false;
                store.location.list = payload.data;
            })
            .addCase(fetchLocations.rejected, (store, { payload }) => {
                store.location.loading = false;
                store.location.error = payload;
            })
            .addMatcher(isAnyOf(logOut.fulfilled, logOut.rejected), (store, { payload }) => {
                gaEvents.logout(store.user.id);
                const chatMessagesCounts = storageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT);
                const chatSavedMessages = storageService.get(STORAGE_KEY_SAVED_USER_MESSAGES);

                if (payload?.forceLogOut) {
                    store.user = {
                        ...initialState.user
                    };
                    store.showAuthModal = true;
                    store.accessToken = '';
                    store.refreshToken = '';
                    store.loading = false;
                    store.error = null;
                    store.registerStepCount = 1;
                } else {
                    store = initialState;
                }

                storageService.clear();
                storageService.add(STORAGE_KEY_CHATS_MESSAGES_COUNT, chatMessagesCounts);
                storageService.add(STORAGE_KEY_SAVED_USER_MESSAGES, chatSavedMessages);
                window.location.replace(APP_URL_HOME);
            });
    }
});

export const authReducers = authSlice.reducer;
export const {
    toggleAuthModal,
    setUserStatusType,
    setAuthToken,
    setAuthRegisterStepCount,
    clearAuthError,
    reduceUserUnreadMessages,
    setUserMessagesLimits,
    setAuthModalCanBeClosed
} = authSlice.actions;
